import "leaflet/dist/leaflet.css";
import "../css/port-current-flow.css";
import { L } from "./vendor.js";
import { watchPortCurrentFlow } from "./port-current-flow-refresh.js";
import { updateConditionsTime } from "./conditions-time.js";
import { applyFlowColor, createParticleLayer, savedFlowColor } from "./great-lakes-conditions.js";
import { formatUnitValue } from "./units.js";
import { html, setHtml } from "./html.js";
import { portCurrentFlowMapUrl, portCurrentFlowView, PORT_CURRENT_FLOW_PARTICLE_DENSITY } from "./port-current-flow-view.js";

const BASE_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const LABELS_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const ATTRIBUTION = html`Tiles &copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>, and the GIS User Community · <a href="https://leafletjs.com/">Leaflet</a>`;

function addAttributionControl(map) {
  const control = L.control({ position: "bottomleft" });
  control.onAdd = () => {
    const container = L.DomUtil.create("div", "port-current-attribution-control");
    const button = L.DomUtil.create("button", "port-current-attribution-button", container);
    button.type = "button";
    button.textContent = "i";
    button.setAttribute("aria-label", "Show map attribution");
    button.setAttribute("aria-expanded", "false");

    const panel = L.DomUtil.create("div", "port-current-attribution-panel", container);
    panel.id = "port-current-attribution-panel";
    panel.hidden = true;
    setHtml(panel, ATTRIBUTION);
    button.setAttribute("aria-controls", panel.id);

    const setExpanded = (expanded) => {
      panel.hidden = !expanded;
      button.setAttribute("aria-expanded", String(expanded));
      button.setAttribute("aria-label", `${expanded ? "Hide" : "Show"} map attribution`);
    };
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);
    L.DomEvent.on(button, "click", () => setExpanded(button.getAttribute("aria-expanded") !== "true"));
    L.DomEvent.on(container, "keydown", (event) => {
      if (event.key !== "Escape" || button.getAttribute("aria-expanded") !== "true") return;
      setExpanded(false);
      button.focus();
    });
    return container;
  };
  control.addTo(map);
}

function addExpandControl(map, mapUrl) {
  const control = L.control({ position: "topright" });
  control.onAdd = () => {
    const container = L.DomUtil.create("div", "port-current-expand-control");
    const link = L.DomUtil.create("a", "port-current-expand-button", container);
    link.href = mapUrl;
    link.setAttribute("aria-label", "Open the main map centered at this current-flow location");
    link.title = "Open the main map";

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    icon.append(path);
    link.append(icon);

    path.setAttribute("d", "M8 3H3v5 M16 3h5v5 M21 16v5h-5 M3 16v5h5");
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);
    return container;
  };
  control.addTo(map);
}

function addCurrentSpeedLegend(map, payload) {
  const control = L.control({ position: "bottomright" });
  let range = null;
  control.onAdd = () => {
    const container = L.DomUtil.create("div", "port-current-speed-legend");
    container.setAttribute("role", "img");
    container.setAttribute("aria-label", "Current speed colour scale");
    const title = L.DomUtil.create("strong", "", container);
    title.textContent = "Current speed";
    L.DomUtil.create("i", "port-current-speed-gradient", container);
    const labels = L.DomUtil.create("div", "port-current-speed-labels", container);
    const low = L.DomUtil.create("span", "", labels);
    low.textContent = "0";
    range = L.DomUtil.create("span", "", labels);
    return container;
  };
  control.addTo(map);
  const update = (nextPayload) => {
    const maximum = Number(nextPayload.speedMaximumMetersPerSecond);
    const rasterVisible = Boolean(nextPayload.speedRasters?.length) && Number.isFinite(maximum) && maximum > 0;
    const container = control.getContainer();
    container.hidden = !rasterVisible;
    if (rasterVisible && range) range.textContent = formatUnitValue(maximum * 3.6, "speed", "kph", { decimals: 1 });
  };
  update(payload);
  return update;
}

function initializeMap(container, payload, status) {
  const field = payload.field;
  if (!field || (!field.points && !(field.rows > 1 && field.columns > 1))) {
    throw new Error("Current field is incomplete");
  }
  const initialView = portCurrentFlowView(payload.portLocation, payload.samplingLocation);
  if (!initialView) throw new Error("Current field has no usable location");
  const zoomOffset = Number(container.dataset.zoomOffset);
  const zoomAdjustment = Number.isFinite(zoomOffset) ? zoomOffset : 0;
  const zoomFor = (view) => Math.max(0, view.zoom + zoomAdjustment);
  const openMapUrl = portCurrentFlowMapUrl(payload.portLocation, payload.samplingLocation, window.location.origin, zoomAdjustment);
  let mapHitArea = container.querySelector("[data-port-current-map-link]");
  if (!mapHitArea) {
    mapHitArea = document.createElement("a");
    mapHitArea.className = "port-current-map-hitarea";
    mapHitArea.dataset.portCurrentMapLink = "";
    mapHitArea.setAttribute("aria-label", "Open the main map centered at this current-flow location");
    container.append(mapHitArea);
  }
  if (openMapUrl) {
    container.parentElement.querySelectorAll("[data-port-current-map-link]")
      .forEach((link) => { link.href = openMapUrl; });
  }

  const map = L.map(container, {
    zoomControl: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    dragging: false,
    touchZoom: false,
    boxZoom: false,
    keyboard: false,
    preferCanvas: true,
    attributionControl: false,
  });
  addAttributionControl(map);
  addExpandControl(map, openMapUrl);
  L.tileLayer(BASE_URL, {
    attribution: ATTRIBUTION,
    maxNativeZoom: 16,
    maxZoom: 19,
  }).addTo(map);
  const speedPane = map.createPane("portCurrentSpeed");
  speedPane.style.zIndex = "250";
  let speedLayers = [];
  function updateRasters(payload) {
    for (const layer of speedLayers) map.removeLayer(layer);
    speedLayers = [];
    for (const raster of payload.speedRasters || []) {
      if (!raster?.imageUrl || !Array.isArray(raster.bounds)) continue;
      speedLayers.push(L.imageOverlay(raster.imageUrl, raster.bounds, {
        pane: "portCurrentSpeed",
        opacity: 0.8,
        interactive: false,
        className: "great-lakes-current-raster",
      }).addTo(map));
    }
  }
  updateRasters(payload);
  const labelsPane = map.createPane("portCurrentLabels");
  labelsPane.style.zIndex = "450";
  L.tileLayer(LABELS_URL, { pane: "portCurrentLabels", maxNativeZoom: 16, maxZoom: 19 }).addTo(map);
  map.setView(initialView.center, zoomFor(initialView), { animate: false });
  const updateSpeedLegend = addCurrentSpeedLegend(map, payload);

  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  applyFlowColor(savedFlowColor());
  const particles = !reducedMotion
    ? createParticleLayer(map, [field], { density: PORT_CURRENT_FLOW_PARTICLE_DENSITY }).addTo(map)
    : null;

  function updateStatus(payload) {
    updateConditionsTime(container.parentElement.querySelector("[data-port-current-flow-time]"), payload.validTime);
    const statusMessages = [];
    if (payload.stale) statusMessages.push("Showing the most recently available current field.");
    if (reducedMotion) statusMessages.push("Animation is disabled by your reduced-motion preference.");
    status.textContent = statusMessages.join(" ");
    status.hidden = !statusMessages.length;
  }
  container.hidden = false;
  updateStatus(payload);
  requestAnimationFrame(() => map.invalidateSize({ pan: false }));
  return {
    update(payload) {
      const nextView = portCurrentFlowView(payload.portLocation, payload.samplingLocation);
      if (!payload.field || !nextView) throw new Error("Current field is incomplete");
      // Replace the particle layer so old trails and water masks cannot survive a new field.
      this.particles?.remove();
      this.particles = !reducedMotion
        ? createParticleLayer(map, [payload.field], { density: PORT_CURRENT_FLOW_PARTICLE_DENSITY }).addTo(map)
        : null;
      updateRasters(payload);
      updateSpeedLegend(payload);
      map.setView(nextView.center, zoomFor(nextView), { animate: false });
      const nextUrl = portCurrentFlowMapUrl(payload.portLocation, payload.samplingLocation, window.location.origin, zoomAdjustment);
      wrapperLinks().forEach((link) => { link.href = nextUrl; });
    },
    particles,
    status: updateStatus,
    remove() { this.particles?.remove(); map.remove(); },
  };

  function wrapperLinks() {
    return container.parentElement.querySelectorAll("[data-port-current-map-link], .port-current-expand-button");
  }
}

function loadMap(container) {
  const wrapper = container.parentElement;
  const status = wrapper.querySelector("[data-port-current-flow-status]");
  watchPortCurrentFlow({
    document, window,
    load: async () => {
      const response = await fetch(container.dataset.currentFlowUrl, {
        headers: { Accept: "application/json" },
        cache: "no-cache",
        signal: AbortSignal.timeout(10000),
      });
      if (!response.ok) throw new Error(`Current field request returned ${response.status}`);
      return response.json();
    },
    render: (payload) => initializeMap(container, payload, status),
    unavailable: (hasMap) => {
      container.hidden = !hasMap;
      status.textContent = hasMap
        ? "Showing the most recently available current field. Updates will retry automatically."
        : "Current flow animation is unavailable at this location right now. Updates will retry automatically.";
      status.hidden = false;
    },
  });
}

export function startPortCurrentFlowMaps() {
  const maps = [...document.querySelectorAll("[data-port-current-map]")];
  if (!maps.length) return;
  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        loadMap(entry.target);
      }
    }, { rootMargin: "300px 0px" });
    maps.forEach((map) => observer.observe(map));
  } else {
    maps.forEach(loadMap);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startPortCurrentFlowMaps, { once: true });
} else {
  startPortCurrentFlowMaps();
}
